import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import CtaSection from '../components/CtaSection';
import TrustStamp from '../components/TrustStamp';
import { 
  ChevronDown, 
  ChevronUp, 
  ArrowRight,
  Activity,
  X,
  Waves,
  Infinity as InfinityIcon,
  Zap,
  Flower2
} from 'lucide-react';

export default function Ptsd() {
  const { lang, t: trans } = useLanguage();
  const [openFaq, setOpenFaq] = useState(null);
  const [cmsData, setCmsData] = useState(null);
  const [cmsFaqs, setCmsFaqs] = useState([]);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/ptsd_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching ptsd_side CMS data:', err);
      }
    };

    const fetchFaqs = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/ofte_stillede_spoergsmaal?term=faq-kategori:ptsd');
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.items)) {
            setCmsFaqs(data.items);
          }
        }
      } catch (err) {
        console.error('Error fetching PTSD FAQs:', err);
      }
    };

    fetchCmsData();
    fetchFaqs();
  }, []);

  const content = {
    da: {
      metaTitle: 'PTSD (Posttraumatisk Stressforstyrrelse) | Nordisk Psykiatri',
      metaDesc: 'Få professionel hjælp og traumebehandling for PTSD hos Nordisk Psykiatri. Evidensbaseret psykoterapi med Prolonged Exposure og ACT.',
      eyebrow: 'PTSD',
      titlePart1: 'Når fortiden',
      titlePart2: 'føles nærværende',
      heroDesc: 'PTSD kan opstå efter en overvældende eller livstruende oplevelse. Minder, følelser og kropslige reaktioner kan blive ved med at dukke op – selv længe efter, at faren ikke længere er der.',
      bookBtn: 'Book en indledende samtale',
      
      whatTitle: 'Hvad er PTSD?',
      whatP1: 'PTSD står for Posttraumatisk Stressforstyrrelse.',
      whatP2: '',
      blobP1: 'PTSD er ikke et tegn på svaghed.',
      blobP2: 'Det er en naturlig reaktion på en unaturlig hændelse.',
      
      lifeTitle: 'Livet med PTSD',
      life1: 'Genoplevelser',
      life2: 'Undgåelse',
      life3: 'Alarmberedskab',
      life4: 'Tanker, følelser og relationer',
      
      memoryTitle: 'Når traumeminder forandres',
      memorySub: 'Det skete kan ikke ændres. Men måden, minderne opleves på, kan forandres.',
      
      step1Num: '01 · Fragmenteret',
      step1Desc: 'Billeder, lyde og kropslige brudstykker uden tydelig afstand.',
      
      step2Num: '02 · Bearbejdet i tryghed',
      step2Desc: 'Minderne forbindes gradvist med ord, sammenhæng og nutid.',
      
      step3Num: '03 · Placeret i fortiden',
      step3Desc: 'Det huskes som noget, der skete dengang.',
      
      treatmentEyebrow: 'Behandling af PTSD',
      treatmentTitle1: 'Psykoterapi er',
      treatmentTitle2: 'førstevalg',
      treatmentDesc: 'Traumefokuseret psykoterapi har den stærkeste dokumentation ved PTSD. Behandlingen tilpasses den enkelte og foregår i et trygt, gradvist tempo.',
      contactBtn: 'Kontakt os',
      
      approachesTitle: 'To tilgange, vi tilbyder',
      
      peTitle: 'Prolonged Exposure (PE)',
      peDesc: 'PE er en evidensbaseret behandling af PTSD. I behandlingen arbejdes gradvist med traumeminder og situationer, som er blevet forbundet med fare eller undgåelse.',
      peTag1: 'Traumeminder',
      peTag2: 'Undgåelse',
      peTag3: 'Tilbage til hverdagen',
      
      actTitle: 'Acceptance & Commitment Therapy (ACT)',
      actDesc: 'ACT arbejder med at skabe et mere fleksibelt forhold til svære tanker, følelser og kropslige reaktioner. Fokus er på at mindske kamp og undgåelse og i stedet kunne handle i retning af det, der er vigtigt.',
      actTag1: 'Accept',
      actTag2: 'Psykologisk fleksibilitet',
      actTag3: 'Værdibaseret handling',
      
      faqTitle: 'Ofte stillede spørgsmål om PTSD',
      faqList: [
        {
          q: 'Hvad er forskellen på en almindelig krisereaktion og PTSD?',
          a: 'I de første uger efter en traumatisk hændelse er det helt normalt at opleve søvnbesvær, uro og påtrængende tanker (akut belastningsreaktion). Ved PTSD aftager disse symptomer ikke af sig selv over tid, men fortsætter i måneder eller år og påvirker dagligdagen betydeligt.'
        },
        {
          q: 'Hvordan foregår traumefokuseret behandling hos jer?',
          a: 'Behandlingen indledes altid med en grundig samtale for at skabe tryghed og overblik. Herefter arbejder vi evidensbaseret med enten Prolonged Exposure (PE) eller Acceptance & Commitment Therapy (ACT) i dit eget tempo, så nervesystemet gradvist afspændes.'
        },
        {
          q: 'Kan man blive helt fri for PTSD?',
          a: 'Mange oplever en markant bedring og kan vende tilbage til en velfungerende hverdag uden konstante alarmreaktioner. Hændelsen vil altid være en del af din historie, men den mister sin akutte, overvældende kraft.'
        }
      ]
    },
    en: {
      metaTitle: 'PTSD (Post-Traumatic Stress Disorder) | Nordisk Psykiatri',
      metaDesc: 'Receive professional help and trauma treatment for PTSD at Nordisk Psykiatri. Evidence-based psychotherapy with Prolonged Exposure and ACT.',
      eyebrow: 'PTSD',
      titlePart1: 'When the past',
      titlePart2: 'feels present',
      heroDesc: 'PTSD can develop following an overwhelming or life-threatening event. Memories, emotions, and physical reactions may continue to surface—long after the danger has passed.',
      bookBtn: 'Book initial consultation',
      
      whatTitle: 'What is PTSD?',
      whatP1: 'PTSD stands for Post-Traumatic Stress Disorder.',
      whatP2: '',
      blobP1: 'PTSD is not a sign of weakness.',
      blobP2: 'It is a natural reaction to an abnormal event.',
      
      lifeTitle: 'Living with PTSD',
      life1: 'Re-experiencing',
      life2: 'Avoidance behavior',
      life3: 'Hyperarousal & alarm',
      life4: 'Thoughts, emotions, and relationships',
      
      memoryTitle: 'When trauma memories change',
      memorySub: 'What happened cannot be changed. But the way memories are experienced can transform.',
      
      step1Num: '01 · Fragmented',
      step1Desc: 'Images, sounds, and physical fragments without clear psychological distance.',
      
      step2Num: '02 · Processed in safety',
      step2Desc: 'Memories are gradually connected with words, context, and present reality.',
      
      step3Num: '03 · Placed in the past',
      step3Desc: 'Remembered as something that happened back then.',
      
      treatmentEyebrow: 'Treatment of PTSD',
      treatmentTitle1: 'Psychotherapy is',
      treatmentTitle2: 'first-line choice',
      treatmentDesc: 'Trauma-focused psychotherapy holds the strongest scientific evidence for PTSD. Treatment is tailored individually and proceeds at a safe, gradual pace.',
      contactBtn: 'Contact us',
      
      approachesTitle: 'Two approaches we offer',
      
      peTitle: 'Prolonged Exposure (PE)',
      peDesc: 'PE is an evidence-based treatment for PTSD. In therapy, we work systematically and gradually with trauma memories and situations that have become associated with danger or avoidance.',
      peTag1: 'Trauma memories',
      peTag2: 'Avoidance',
      peTag3: 'Back to everyday life',
      
      actTitle: 'Acceptance & Commitment Therapy (ACT)',
      actDesc: 'ACT focuses on fostering a more flexible relationship with difficult thoughts, emotions, and bodily reactions. The goal is to reduce struggle and avoidance and take values-based action.',
      actTag1: 'Acceptance',
      actTag2: 'Psychological flexibility',
      actTag3: 'Value-based action',
      
      faqTitle: 'Frequently asked questions about PTSD',
      faqList: [
        {
          q: 'What is the difference between an acute crisis reaction and PTSD?',
          a: 'In the initial weeks following a traumatic event, experiencing sleep disruption, restlessness, and intrusive thoughts is completely natural (acute stress reaction). In PTSD, these symptoms do not fade on their own over time, but persist for months or years, significantly impairing daily life.'
        },
        {
          q: 'How does trauma-focused treatment take place with you?',
          a: 'Treatment always begins with a comprehensive consultation to establish safety and overview. We then proceed with evidence-based Prolonged Exposure (PE) or ACT at your own pace, allowing the nervous system to settle.'
        },
        {
          q: 'Can one fully recover from PTSD?',
          a: 'Many experience substantial relief and return to a functioning everyday life free from constant alarm states. The event remains part of your story, but loses its overwhelming power.'
        }
      ]
    }
  };

  const t = content[lang] || content.en || content.da;

  useDocumentMetadata(t.metaTitle, t.metaDesc, { lang });

  const getLocalizedValue = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (field[lang] !== undefined && field[lang]) return field[lang];
    if (lang === 'en' && field.sv) return field.sv;
    return field.da !== undefined ? field.da : '';
  };

  const getVal = (cmsField, fallback) => {
    if (!cmsField) return fallback;
    if (typeof cmsField === 'string') return cmsField || fallback;
    if (cmsField[lang] !== undefined && cmsField[lang]) {
      return cmsField[lang];
    }
    if (lang === 'en' && cmsField.sv) {
      return cmsField.sv;
    }
    if (cmsField.da !== undefined && lang === 'da') {
      return cmsField.da || fallback;
    }
    return fallback;
  };

  const getOptimizedImageUrl = (url, width = 800) => {
    if (!url) return '';
    if (url.includes('/cdn-cgi/image/')) return url;
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  const getImageUrl = (field, fallback, width = 800) => {
    if (Array.isArray(field) && field.length > 0 && field[0]) {
      return getOptimizedImageUrl(field[0], width);
    }
    if (typeof field === 'string' && field) {
      return getOptimizedImageUrl(field, width);
    }
    return fallback;
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Fallback image assets
  const defaultHeroImage = "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-sep-22-2026-08-40-19-pm_46b62528.png";
  const defaultWhatBlobImage = "https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-2-sep-2026-10-34-50.png";
  const defaultBrainIllustration = "https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/ptsd-hjernen-illustration.png";
  const defaultPeImage = "https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-2-sep-2026-09-54-36.png";
  const defaultActImage = "https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/chatgpt-image-2-sep-2026-09-54-36_6bb64ad5.png";

  return (
    <div className="bg-white min-h-screen text-[#102735] font-sans selection:bg-[#0c5647]/15 selection:text-[#0c5647]">
      
      {/* ============================================= */}
      {/* 1. PTSD HERO */}
      {/* ============================================= */}
      <section className="pt-12 sm:pt-16 lg:pt-24 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20 items-center">
            
            <div className="flex flex-col items-start">
              <div className="text-[#0c5647] text-xs sm:text-sm font-semibold uppercase tracking-wider mb-4 sm:mb-5">
                {getVal(cmsData?.hero_badge, t.eyebrow)}
              </div>

              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-[#102936] leading-tight tracking-tight mb-0">
                {cmsData?.hero_overskrift ? (
                  getVal(cmsData.hero_overskrift, '')
                ) : (
                  <>
                    {t.titlePart1}<br className="hidden sm:inline" />
                    {t.titlePart2}
                  </>
                )}
              </h1>

              <div className="w-12 h-0.5 bg-[#0c5647] my-6 sm:my-7"></div>

              <p className="max-w-[500px] text-base sm:text-lg text-[#102735]/85 leading-relaxed mb-8">
                {getVal(cmsData?.hero_undertekst, t.heroDesc)}
              </p>

              <div className="flex flex-wrap items-center gap-4">
                {(() => {
                  const bookingHref = cmsData?.hero_cta_link || "/kontakt";
                  const isExt = typeof bookingHref === 'string' && (bookingHref.startsWith('http://') || bookingHref.startsWith('https://'));
                  const btnLabel = getVal(cmsData?.hero_cta_tekst, t.bookBtn);
                  return isExt ? (
                    <a
                      href={bookingHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                    >
                      {btnLabel}
                    </a>
                  ) : (
                    <Link
                      to={bookingHref}
                      className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                    >
                      {btnLabel}
                    </Link>
                  );
                })()}

                <Link
                  to="/priser"
                  className="inline-flex items-center justify-center rounded-full bg-transparent hover:bg-petrol-dark/5 text-petrol-dark border border-petrol-dark/60 hover:border-petrol-dark px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold transition-all duration-200 shadow-xs"
                >
                  {lang === 'da' ? 'Se priser' : 'See prices'}
                </Link>
              </div>

              {/* Fagligt gennemgået / Review trust stamp */}
              <TrustStamp
                text={cmsData?.fagligt_gennemgaaet ? getVal(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, Clinical Psychologist | Updated September 2026')}
              />
            </div>

            <div className="w-full">
              <div className="w-full min-h-[340px] sm:min-h-[420px] lg:min-h-[470px] rounded-[32px] overflow-hidden shadow-sm bg-sand-light">
                <img
                  src={getImageUrl(cmsData?.hero_billede, defaultHeroImage, 1200)}
                  alt={lang === 'da' ? 'Roligt landskab som illustration til PTSD' : 'Peaceful landscape illustrating PTSD'}
                  className="w-full h-full min-h-[340px] sm:min-h-[420px] lg:min-h-[470px] object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ============================================= */}
      {/* 2. HVAD ER PTSD? */}
      {/* ============================================= */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-20 items-center">
            
            <div>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#102936] leading-tight tracking-tight mb-0">
                {getVal(cmsData?.hvad_er_overskrift, t.whatTitle)}
              </h2>

              <div className="w-12 h-0.5 bg-[#0c5647] my-6 sm:my-7"></div>

              <p className={`text-base sm:text-lg text-[#102735]/85 leading-relaxed ${getVal(cmsData?.hvad_er_tekst_2, t.whatP2) ? 'mb-4' : 'mb-0'}`}>
                {getVal(cmsData?.hvad_er_tekst_1, t.whatP1)}
              </p>

              {getVal(cmsData?.hvad_er_tekst_2, t.whatP2) ? (
                <p className="text-base sm:text-lg text-[#102735]/85 leading-relaxed mb-0">
                  {getVal(cmsData?.hvad_er_tekst_2, t.whatP2)}
                </p>
              ) : null}
            </div>

            <div 
              className="relative bg-[#CFE0DB] rounded-[35px] sm:rounded-[45px] p-8 sm:p-12 lg:p-14 grid grid-cols-1 sm:grid-cols-[110px_1fr] lg:grid-cols-[130px_1fr] items-center gap-6 sm:gap-8"
              style={{ backgroundColor: '#CFE0DB' }}
            >
              <div className="w-[90px] h-[90px] sm:w-[110px] sm:h-[110px] lg:w-[120px] lg:h-[120px] flex items-center justify-center mx-auto sm:mx-0">
                <img 
                  src={getImageUrl(cmsData?.hvad_er_blob_billede, defaultWhatBlobImage, 300)}
                  alt="Illustration"
                  className="w-full h-full object-contain mix-blend-multiply"
                  loading="lazy"
                />
              </div>

              <div className="text-base sm:text-lg text-[#102735] leading-relaxed">
                <p className="mb-2 font-medium">
                  {getVal(cmsData?.hvad_er_blob_titel, t.blobP1)}
                </p>
                <p className="mb-0 text-[#102735]/85">
                  {getVal(cmsData?.hvad_er_blob_tekst, t.blobP2)}
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>


      {/* ============================================= */}
      {/* 3. LIVET MED PTSD */}
      {/* ============================================= */}
      <section className="py-10 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#102936] leading-tight tracking-tight mb-0">
              {getVal(cmsData?.livet_overskrift, t.lifeTitle)}
            </h2>
            <div className="w-12 h-0.5 bg-[#0c5647] mx-auto mt-4 sm:mt-5"></div>
          </div>

          <div className="p-8 sm:p-10 lg:py-10 lg:px-14 bg-[#f6f2eb] rounded-[32px] sm:rounded-[60px] lg:rounded-[100px] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1.2fr] items-center text-center gap-6 sm:gap-8 lg:gap-5">
            
            {/* Symbol 1: Genoplevelser (Tangle) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center p-1">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/rod.png"
                  alt="Genoplevelser symbol"
                  className="w-full h-full object-contain mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <strong className="font-display text-base sm:text-lg font-normal text-[#102936]">
                {getVal(cmsData?.livet_punkt_1, t.life1)}
              </strong>
            </div>

            <div className="hidden lg:block text-[#0c5647] text-xl font-light">
              —
            </div>

            {/* Symbol 2: Undgåelse (Cross / X) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center p-1">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/kryds.png"
                  alt="Undgåelse symbol"
                  className="w-full h-full object-contain mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <strong className="font-display text-base sm:text-lg font-normal text-[#102936]">
                {getVal(cmsData?.livet_punkt_2, t.life2)}
              </strong>
            </div>

            <div className="hidden lg:block text-[#0c5647] text-xl font-light">
              —
            </div>

            {/* Symbol 3: Alarmberedskab (Wave) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 relative flex items-center justify-center p-1">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/ba-lge.png"
                  alt="Alarmberedskab symbol"
                  className="w-full h-full object-contain mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <strong className="font-display text-base sm:text-lg font-normal text-[#102936]">
                {getVal(cmsData?.livet_punkt_3, t.life3)}
              </strong>
            </div>

            <div className="hidden lg:block text-[#0c5647] text-xl font-light">
              —
            </div>

            {/* Symbol 4: Tanker, følelser og relationer (Overlapping Circles) */}
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 relative flex items-center justify-center p-1">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/7d97f2cb-1a26-4176-9980-5fb38f404ca8/cirkler.png"
                  alt="Tanker, følelser og relationer symbol"
                  className="w-full h-full object-contain mix-blend-multiply"
                  loading="lazy"
                />
              </div>
              <strong className="font-display text-base sm:text-lg font-normal text-[#102936] leading-snug">
                {getVal(cmsData?.livet_punkt_4, (
                  lang === 'da' ? (
                    <>Tanker, følelser<br className="hidden sm:inline" />og relationer</>
                  ) : (
                    <>Tankar, känslor<br className="hidden sm:inline" />och relationer</>
                  )
                ))}
              </strong>
            </div>

          </div>

        </div>
      </section>


      {/* ============================================= */}
      {/* 4. NÅR TRAUMEMINDER FORANDRES */}
      {/* ============================================= */}
      <section 
        className="py-16 sm:py-20 lg:py-24 border-y border-[#d8dfd9]/60"
        style={{
          background: 'radial-gradient(circle at 15% 15%, #e3f3f5, transparent 35%), linear-gradient(90deg, #eef8fa, #ffffff)'
        }}
      >
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          
          <div className="max-w-3xl mb-12 sm:mb-14">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#102936] leading-tight tracking-tight mb-0">
              {getVal(cmsData?.minder_overskrift, t.memoryTitle)}
            </h2>

            <div className="w-12 h-0.5 bg-[#0c5647] my-5 sm:my-6"></div>

            <p className="text-base sm:text-lg text-[#102735]/85 leading-relaxed">
              {getVal(cmsData?.minder_undertekst, t.memorySub)}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-0">
            
            {/* Step 1 */}
            <div className="md:pr-8 lg:pr-12 md:border-r border-[#cfd9db]">
              <span className="block text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#0c5647] mb-3">
                {getVal(cmsData?.minder_trin_1_titel, t.step1Num)}
              </span>
              <p className="text-sm sm:text-base text-[#102735]/80 leading-relaxed mb-6">
                {getVal(cmsData?.minder_trin_1_tekst, t.step1Desc)}
              </p>
              <div className="min-h-[55px] text-[#0c5647] text-lg tracking-[8px] font-mono select-none">
                ─ ─ &nbsp; ─<br />
                &nbsp; ─ ─ &nbsp; ─<br />
                ─ &nbsp; ─ ─
              </div>
            </div>

            {/* Step 2 */}
            <div className="md:px-8 lg:px-12 md:border-r border-[#cfd9db]">
              <span className="block text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#0c5647] mb-3">
                {getVal(cmsData?.minder_trin_2_titel, t.step2Num)}
              </span>
              <p className="text-sm sm:text-base text-[#102735]/80 leading-relaxed mb-6">
                {getVal(cmsData?.minder_trin_2_tekst, t.step2Desc)}
              </p>
              <div className="min-h-[55px] text-[#0c5647] text-lg tracking-[4px] font-mono select-none">
                ─────┐────<br />
                &nbsp;&nbsp;&nbsp;└──────<br />
                ─────────
              </div>
            </div>

            {/* Step 3 */}
            <div className="md:pl-8 lg:pl-12">
              <span className="block text-xs sm:text-sm font-semibold uppercase tracking-wider text-[#0c5647] mb-3">
                {getVal(cmsData?.minder_trin_3_titel, t.step3Num)}
              </span>
              <p className="text-sm sm:text-base text-[#102735]/80 leading-relaxed mb-6">
                {getVal(cmsData?.minder_trin_3_tekst, t.step3Desc)}
              </p>
              <div className="min-h-[55px] text-[#0c5647] text-lg tracking-[2px] font-mono select-none">
                ─────────────<br />
                ─────────────<br />
                ─────────────
              </div>
            </div>

          </div>

        </div>
      </section>


      {/* ============================================= */}
      {/* 5. BEHANDLING AF PTSD */}
      {/* ============================================= */}
      <section className="py-16 sm:py-20 lg:py-28 bg-white">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-14 items-center">
            
            <div className="flex flex-col items-start">
              <div className="text-[#0c5647] text-xs sm:text-sm font-semibold uppercase tracking-wider mb-2">
                {getVal(cmsData?.behandling_badge, t.treatmentEyebrow)}
              </div>

              <div className="w-12 h-0.5 bg-[#0c5647] my-5 sm:my-6"></div>

              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#0c5647] leading-tight tracking-tight mb-6">
                {cmsData?.behandling_overskrift ? (
                  getVal(cmsData.behandling_overskrift, '')
                ) : (
                  <>
                    {t.treatmentTitle1}<br />
                    {t.treatmentTitle2}
                  </>
                )}
              </h2>

              <p className="text-base sm:text-lg text-[#102735]/85 leading-relaxed mb-8">
                {getVal(cmsData?.behandling_beskrivelse, t.treatmentDesc)}
              </p>

              <Link
                to={cmsData?.behandling_knap_link || "/kontakt"}
                className="inline-flex items-center justify-center gap-3 bg-gradient-to-br from-[#063d34] to-[#0c5647] hover:from-[#0c5647] hover:to-[#063d34] text-white rounded-full px-7 py-4 text-base font-semibold shadow-xs transition-all duration-300 hover:scale-[1.02]"
              >
                <span>{getVal(cmsData?.behandling_knap_tekst, t.contactBtn)}</span>
                <span>→</span>
              </Link>
            </div>

            <div className="relative min-h-[380px] sm:min-h-[480px] lg:min-h-[530px] flex items-center justify-center">
              <img
                src={getImageUrl(cmsData?.behandling_billede, defaultBrainIllustration, 900)}
                alt="Illustration af en hjerne"
                className="w-full max-w-[540px] max-h-[480px] sm:max-h-[520px] object-contain"
                loading="lazy"
              />
            </div>

          </div>
        </div>
      </section>


      {/* ============================================= */}
      {/* 6. TO TILGANGE, VI TILBYDER */}
      {/* ============================================= */}
      <section className="py-12 sm:py-16 lg:pb-24 bg-white">
        <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
          
          <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-normal text-[#0c5647] leading-tight tracking-tight mb-0">
              {getVal(cmsData?.tilgange_overskrift, t.approachesTitle)}
            </h2>
            <div className="w-12 h-0.5 bg-[#0c5647] mx-auto my-5"></div>
          </div>

          <div className="space-y-7">
            
            {/* Approach 1: PE */}
            <div 
              className="rounded-[26px] p-8 sm:p-12 lg:p-14"
              style={{
                background: 'linear-gradient(135deg, #f2f3e4, #edf0dd)'
              }}
            >
              <div>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-7 items-start sm:items-center mb-5">
                  <span className="font-display text-[#0c5647] text-2xl sm:text-3xl pr-0 sm:pr-7 border-b sm:border-b-0 sm:border-r border-[#b9c4bb]">
                    01
                  </span>
                  <h3 className="font-display text-xl sm:text-2xl font-normal text-[#0c5647] leading-tight mb-0">
                    {getVal(cmsData?.pe_titel, t.peTitle)}
                  </h3>
                </div>

                <p className="max-w-[760px] text-base sm:text-lg text-[#102735]/85 leading-relaxed mb-6">
                  {getVal(cmsData?.pe_beskrivelse, t.peDesc)}
                </p>

                <div className="border-t border-[#cbd1ca] pt-5 flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-[#102735]/75">
                  <span>{getVal(cmsData?.pe_tag_1, t.peTag1)}</span>
                  <span className="text-[#82978f]">•</span>
                  <span>{getVal(cmsData?.pe_tag_2, t.peTag2)}</span>
                  <span className="text-[#82978f]">•</span>
                  <span>{getVal(cmsData?.pe_tag_3, t.peTag3)}</span>
                </div>
              </div>
            </div>

            {/* Approach 2: ACT */}
            <div 
              className="rounded-[26px] p-8 sm:p-12 lg:p-14"
              style={{
                background: 'linear-gradient(135deg, #f8f5ef, #f3eee8)'
              }}
            >
              <div>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-7 items-start sm:items-center mb-5">
                  <span className="font-display text-[#0c5647] text-2xl sm:text-3xl pr-0 sm:pr-7 border-b sm:border-b-0 sm:border-r border-[#b9c4bb]">
                    02
                  </span>
                  <h3 className="font-display text-xl sm:text-2xl font-normal text-[#0c5647] leading-tight mb-0">
                    {getVal(cmsData?.act_titel, t.actTitle)}
                  </h3>
                </div>

                <p className="max-w-[760px] text-base sm:text-lg text-[#102735]/85 leading-relaxed mb-6">
                  {getVal(cmsData?.act_beskrivelse, t.actDesc)}
                </p>

                <div className="border-t border-[#cbd1ca] pt-5 flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm text-[#102735]/75">
                  <span>{getVal(cmsData?.act_tag_1, t.actTag1)}</span>
                  <span className="text-[#82978f]">•</span>
                  <span>{getVal(cmsData?.act_tag_2, t.actTag2)}</span>
                  <span className="text-[#82978f]">•</span>
                  <span>{getVal(cmsData?.act_tag_3, t.actTag3)}</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>


      {/* ============================================= */}
      {/* 8. BUND CTA */}
      {/* ============================================= */}
      <CtaSection />

    </div>
  );
}
